import type { GameStateManager } from '../game/GameState';
import type { GameData, RealityKillResult, GameSpeed } from '../game/types';
import { AudioManager } from '../audio/AudioManager';

export class RealityKillModal {
  private keyDownHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(
    private container: HTMLElement,
    private gameState: GameStateManager,
    private gameData: GameData,
    private onClose: () => void,
    private onSpeedRestore?: (speed: GameSpeed) => void
  ) {
    this.setup();
  }

  private setup(): void {
    this.container.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).classList.contains('modal-backdrop')) {
        this.hide();
      }
    });
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (this.container.classList.contains('hidden')) return;

    if (event.code === 'Enter' || event.code === 'Escape' || event.code === 'Space') {
      event.preventDefault();
      event.stopPropagation();
      this.hide();
    }
  }

  show(result: RealityKillResult): void {
    this.render(result);
    this.container.classList.remove('hidden');
    this.gameState.setPausedByModal(true);
    
    AudioManager.getInstance().playEffect();
    
    this.keyDownHandler = (e: KeyboardEvent) => this.handleKeyDown(e);
    document.addEventListener('keydown', this.keyDownHandler);
    
    const modalContent = this.container.querySelector('.modal-content') as HTMLElement;
    if (modalContent) {
      modalContent.focus();
    }
  }

  hide(): void {
    const result = this.gameState.getPendingRealityKill();
    
    if (result) {
      // Find and remove the researcher
      const researchers = this.gameState.getResearchers();
      const victim = researchers.find(r => r.data.name === result.researcherName);
      
      if (victim) {
        // Cancel any ongoing actions
        if (victim.currentAction === 'Research' || victim.currentAction === 'Contain') {
          // Release any reserved computing
          if (victim.researchTypes) {
            const researchTypes = this.gameData.researchTypes.filter(rt => 
              victim.researchTypes!.includes(rt.id)
            );
            let totalComputing = 0;
            for (const rt of researchTypes) {
              for (const cost of rt.costs) {
                if (cost.computation) {
                  totalComputing += cost.computation;
                }
              }
            }
            if (totalComputing > 0) {
              this.gameState.releaseComputing(totalComputing);
            }
          }
        }
        
        // Remove the researcher
        this.gameState.removeResearcher(victim.id);
        
        // Clear selection if this researcher was selected
        const selected = this.gameState.getSelectedEntity();
        if (selected.type === 'researcher' && selected.id === victim.id) {
          this.gameState.setSelectedEntity(null, null);
        }
      }
    }
    
    this.container.classList.add('hidden');
    this.gameState.setPausedByModal(false);
    const restoredSpeed = this.gameState.restorePauseStateAfterModal();
    if (restoredSpeed !== null && this.onSpeedRestore) {
      this.onSpeedRestore(restoredSpeed);
    }
    this.gameState.dequeueRealityKill();
    
    if (this.keyDownHandler) {
      document.removeEventListener('keydown', this.keyDownHandler);
      this.keyDownHandler = null;
    }
    
    // Defer onClose to next tick to prevent synchronous re-showing of same modal
    setTimeout(() => {
      this.onClose();
    }, 0);
  }

  isOpen(): boolean {
    return !this.container.classList.contains('hidden');
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private render(result: RealityKillResult): void {
    const researcherName = result.researcherName?.trim() || 'Unknown Researcher';
    
    this.container.innerHTML = `
      <div class="modal-backdrop"></div>
      <div class="modal-content reality-kill-modal" tabindex="0">
        <div class="modal-header">
          <h2>💀 Claimed by the Breach</h2>
          <button class="modal-close" data-action="close">×</button>
        </div>
        <div class="modal-body">
          <div class="death-message">
            <p class="victim-name"><strong>${this.escapeHtml(researcherName)}</strong></p>
            <p class="death-description">
              The fabric of reality warped around them, and they were unmade. 
              Not killed, not destroyed—simply removed from existence, 
              as if they had never been.
            </p>
            <p class="death-note">
              The other researchers sense something is wrong, but they cannot remember what they've lost.
            </p>
          </div>
        </div>
        <div class="modal-footer">
          <button class="button-confirm" data-action="close">Accept the Loss</button>
        </div>
      </div>
    `;

    const closeButton = this.container.querySelector('.modal-close') as HTMLElement;
    if (closeButton) {
      closeButton.addEventListener('click', () => this.hide());
    }

    const footerCloseButton = this.container.querySelector('.modal-footer [data-action="close"]') as HTMLElement;
    if (footerCloseButton) {
      footerCloseButton.addEventListener('click', () => this.hide());
    }
  }
}
